import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ChevronRight, Loader2, TrendingUp, CheckCircle2, Zap } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

export default function StaffScoreHero() {
  const navigate = useNavigate();
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hadError, setHadError] = useState(false);

  useEffect(() => {
    let mounted = true;
    // Safety timeout: never spin forever — force exit loading after 15s.
    const safetyTimer = setTimeout(() => {
      if (mounted) {
        setLoading(false);
        setHadError(true);
      }
    }, 15000);

    (async () => {
      try {
        const data = await apiRequest('/performance/my-score').catch(() => null);
        if (!mounted) return;
        if (!data) setHadError(true);
        setScoreData(data);
      } finally {
        if (mounted) setLoading(false);
        clearTimeout(safetyTimer);
      }
    })();

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
    };
  }, []);

  const getScoreGradient = (score) => {
    if (score >= 8) return 'from-green-500 to-emerald-600';
    if (score >= 6) return 'from-amber-500 to-orange-600';
    if (score >= 4) return 'from-orange-500 to-red-500';
    return 'from-red-500 to-red-700';
  };

  const getScoreLabel = (score) => {
    if (score >= 9) return 'Outstanding';
    if (score >= 8) return 'Excellent';
    if (score >= 7) return 'Very Good';
    if (score >= 6) return 'Good';
    if (score >= 5) return 'Average';
    if (score >= 4) return 'Needs Improvement';
    return 'Below Expectations';
  };

  // Don't show for CEO/MD, or when the API failed entirely.
  if (!loading && (scoreData?.score === null || hadError || !scoreData)) {
    return null;
  }

  const score = scoreData?.score || 0;

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-slate-600 to-slate-700 text-white mb-6">
        <CardContent className="p-4 flex justify-center items-center min-h-[80px]">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className={`bg-gradient-to-br ${getScoreGradient(score)} text-white mb-6 overflow-hidden relative`}
      data-testid="staff-score-hero"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      
      <CardContent className="p-4 relative z-10">
        <div className="flex items-center justify-between">
          {/* Score Section */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <div className="text-center">
                  <span className="text-2xl font-bold">{score || '-'}</span>
                  <span className="text-sm">/10</span>
                </div>
              </div>
              {score >= 8 && (
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                  <Star className="w-4 h-4 text-yellow-800 fill-yellow-800" />
                </div>
              )}
            </div>
            <div>
              <h2 className="text-base font-bold">Your Performance Score</h2>
              <p className="text-white/80 text-sm">
                {score > 0 ? getScoreLabel(score) : 'No data yet'}
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="hidden sm:flex items-center gap-3">
            {scoreData?.tasks_total > 0 && (
              <div className="text-center px-3 py-1 bg-white/10 rounded-lg">
                <div className="flex items-center gap-1 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-semibold">{scoreData.tasks_completed}/{scoreData.tasks_total}</span>
                </div>
                <p className="text-xs text-white/70">Tasks</p>
              </div>
            )}
            {scoreData?.engagement_score && (
              <div className="text-center px-3 py-1 bg-white/10 rounded-lg">
                <div className="flex items-center gap-1 text-sm">
                  <Zap className="w-4 h-4" />
                  <span className="font-semibold">{scoreData.engagement_score}</span>
                </div>
                <p className="text-xs text-white/70">Engagement</p>
              </div>
            )}
          </div>

          {/* View Details Button */}
          <Button 
            variant="secondary" 
            size="sm"
            onClick={() => navigate('/my-performance')}
            className="bg-white/20 hover:bg-white/30 text-white border-0"
          >
            View Details
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
